import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { NosProduct } from '../models/product.model';
import { Product } from '../../../models/product.model';
import { CategoriesService } from '../../../../modules/admin/ecommerce/services/categories.service';

@Injectable({
  providedIn: 'root'
})
export class ProductsServiceAdapter {
  private _products = new BehaviorSubject<Product[]>([]);

  constructor(
    private _httpClient: HttpClient,
    private categoriesService: CategoriesService
  ) {
    // Initialize the BehaviorSubject by fetching products
    this.fetchProducts().subscribe();
  }

  /**
   * Fetches products and processes them to include categories.
   * Only called once, during initialization.
   */
  public fetchProducts(): Observable<Product[]> {
    return this._httpClient.get<NosProduct[]>('api/products/').pipe(
      map((nosProducts: NosProduct[]) =>
        nosProducts.map(nosProduct => {
          return {
            name: nosProduct.nome,
            storeId: nosProduct.id_funcionario.toString(),
            sku: nosProduct.codigo_barras || '',
            price: { EUR: nosProduct.preco_final },
            priceWithoutTax: { EUR: nosProduct.preco },
            imgURL: nosProduct.caminho_imagem || '',
            categories: [], // Categories processed later
            description: `Registered on: ${nosProduct.data_registo}`,
            active: nosProduct.estado === 'REGISTADO',
            stock: nosProduct.stock,
            photo: '',
            addOns: [],
            id: nosProduct.id_produto.toString(),
            id_produto: nosProduct.id_produto,
            numero: nosProduct.numero,
            id_taxa: nosProduct.id_taxa,
            id_classificacao: nosProduct.id_classificacao,
            id_familiaproduto: nosProduct.id_familiaproduto,
            id_subfamiliaproduto: nosProduct.id_subfamiliaproduto,
            barCode: nosProduct.codigo_barras
          };
        })
      ),
      switchMap(products =>
        this.categoriesService.fetchCategories().pipe(
          map(categories =>
            products.map(product => ({
              ...product,
              categories: categories.filter(category => {
                if (category.id.toString() === product.id_familiaproduto?.toString() && !product.id_subfamiliaproduto) {
                  return true;
                }
                return category.id.toString() === product.id_subfamiliaproduto?.toString();
              })
            }))
          )
        )
      ),
      tap(result => {
        this._products.next(result);
      })
    );
  }

  /**
   * Returns all products as an observable.
   */
  getAllProducts(): Observable<Product[]> {
    return this._products.asObservable();
  }

  /**
   * Clears the products cache by resetting the BehaviorSubject to an empty array.
   * This should be called when a user logs out.
   */
  clearProductsCache(): void {
    this._products.next([]);
  }
}
